/**
 * Pagination — Navegação de páginas com elipsis e seletor de itens por página
 */
import { T } from '../../styles/tokens';

interface PaginationProps {
  /** Página atual (1-indexed) */
  page: number;
  /** Total de páginas */
  totalPages: number;
  /** Callback ao mudar página */
  onPageChange: (page: number) => void;
  /** Opções de itens por página (opcional) */
  perPageOptions?: number[];
  /** Itens por página atual */
  perPage?: number;
  /** Callback ao mudar itens por página */
  onPerPageChange?: (perPage: number) => void;
}

/** Gera array de números de página com elipsis */
function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [1];
  if (current > 3) pages.push('...');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

const btnBase: any = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 32,
  height: 32,
  borderRadius: T.radiusMd,
  border: `1px solid ${T.border}`,
  background: 'transparent',
  color: T.text,
  fontSize: T.textSm,
  fontWeight: T.weightMedium,
  cursor: 'pointer',
  transition: `all ${T.transitionFast}`,
};

export function Pagination({ page, totalPages, onPageChange, perPageOptions, perPage, onPerPageChange }: PaginationProps) {
  if (totalPages <= 1 && !perPageOptions) return null;

  const pages = getPageNumbers(page, totalPages);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: T.space4, flexWrap: 'wrap' }}>
      {/* Seletor de itens por página */}
      {perPageOptions && onPerPageChange && (
        <div style={{ display: 'flex', alignItems: 'center', gap: T.space2 }}>
          <span style={{ fontSize: T.textSm, color: T.textMuted }}>Por página:</span>
          <select
            value={perPage}
            onChange={(e) => onPerPageChange(Number((e.target as HTMLSelectElement).value))}
            style={{
              background: T.bgCard,
              border: `1px solid ${T.border}`,
              borderRadius: T.radiusMd,
              color: T.text,
              fontSize: T.textSm,
              padding: `${T.space1}px ${T.space2}px`,
              outline: 'none',
            }}
          >
            {perPageOptions.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      )}

      {/* Navegação de páginas */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: T.space1 }}>
          {/* Prev */}
          <button
            onClick={() => page > 1 && onPageChange(page - 1)}
            disabled={page <= 1}
            style={{ ...btnBase, opacity: page <= 1 ? 0.3 : 1, cursor: page <= 1 ? 'default' : 'pointer' }}
          >
            ‹
          </button>

          {/* Números */}
          {pages.map((p, i) =>
            p === '...' ? (
              <span key={`e${i}`} style={{ color: T.textMuted, fontSize: T.textSm, padding: `0 ${T.space1}px` }}>…</span>
            ) : (
              <button
                key={p}
                onClick={() => onPageChange(p as number)}
                style={{
                  ...btnBase,
                  background: p === page ? T.primary : 'transparent',
                  borderColor: p === page ? T.primary : T.border,
                  color: p === page ? '#fff' : T.text,
                  fontWeight: p === page ? T.weightSemibold : T.weightMedium,
                }}
              >
                {p}
              </button>
            )
          )}

          {/* Next */}
          <button
            onClick={() => page < totalPages && onPageChange(page + 1)}
            disabled={page >= totalPages}
            style={{ ...btnBase, opacity: page >= totalPages ? 0.3 : 1, cursor: page >= totalPages ? 'default' : 'pointer' }}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
